import { useMemo } from 'react';
import { Banner, Box, Text } from '@customerio/pluma-components/react';
import { GroupedChecklist, type CheckboxGroup } from '@/components/grouped-checklist';
import { useWatch } from '@/components/form';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { isForceDisabled } from '@/services/db/webhook/utils';
import { getWebhookEventGroups } from '@/utils/webhook-events';
import type { ReportingWebhookFormApi } from '../types';

const NO_EVENTS_BANNER_TITLE = 'Heads up! You have no events chosen.';
const NO_EVENTS_BANNER_BODY = 'If no events are selected, this webhook will be disabled.';

type WebhookEventsFieldProps = {
  form: ReportingWebhookFormApi;
  isDisabled: boolean;
  /** The webhook has not been created yet, so there is no live delivery for it to lose. */
  isNewWebhook: boolean;
};

/** The event picker, plus the warning an existing webhook gets when its last event is cleared. */
export function WebhookEventsField({ form, isDisabled, isNewWebhook }: WebhookEventsFieldProps) {
  const productTaxonomy = useProductTaxonomy();
  const groups = useMemo<CheckboxGroup[]>(() => [...getWebhookEventGroups({ productTaxonomy })], [productTaxonomy]);
  const events = useWatch({ control: form.control, name: 'events' });

  return (
    <Box as="fieldset" flex={1} minW="0">
      <Text as="legend" text="product-h4" mb="150">
        Select Events
      </Text>
      {isForceDisabled(events) && !isNewWebhook ? (
        <Banner
          variant="caution"
          icon={null}
          my="150"
          title={NO_EVENTS_BANNER_TITLE}
          description={NO_EVENTS_BANNER_BODY}
          data-test-webhook-no-events-warning
        />
      ) : null}
      <GroupedChecklist
        groups={groups}
        selectedItems={events}
        isDisabled={isDisabled}
        onChange={(delta) => {
          form.setValue('events', delta.selectedItems, { shouldDirty: true, shouldValidate: true });
        }}
      />
    </Box>
  );
}
